import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, SkeletonTable } from '@/components/ui';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useScopedAccounts } from './hierarchy/scopedAccounts/useScopedAccounts';
import { useScopedAccountWrites } from './hierarchy/scopedAccounts/useScopedAccountWrites';
import { useBulkLevelAssign } from './hierarchy/scopedAccounts/useBulkLevelAssign';
import { useRowTransfer } from './hierarchy/scopedAccounts/useRowTransfer';
import { ScopedAccountsTable } from './hierarchy/scopedAccounts/ScopedAccountsTable';
import { ScopedAccountDialogs } from './hierarchy/scopedAccounts/ScopedAccountDialogs';
import { BulkLevelAssignDialog } from './hierarchy/scopedAccounts/BulkLevelAssignDialog';
import { RowTransferDialog } from './hierarchy/scopedAccounts/RowTransferDialog';
import { AccountsScreen, type AccountsTab } from './accounts/shared/AccountsScreen';
import { AccountsBulkBar, type AccountsBulkAction } from './accounts/shared/AccountsBulkBar';
import { AccountsPagination } from './accounts/shared/AccountsPagination';
import { AccountAuditPanel } from './audit/AccountAuditPanel';

const TABLE_COLUMNS = 6;

/**
 * EAA-1820 (screen unification pass): `/console/hierarchy/accounts` — the
 * org-scoped account browser, now rendered through the SAME `AccountsScreen`
 * the realm `/console/accounts` uses (see `AccountsPage.tsx`'s docstring for
 * the full reversal note).
 *
 * WHAT STAYS DIFFERENT, per the ticket's own governing rule ("Khác nhau chỉ ở
 * ngữ cảnh"): the "Phạm vi" bar (`scopeBar` — this screen has a branch to
 * narrow; the realm screen reads the whole realm and has none), the "Level &
 * vai trò" column (`ScopedAccountsTable`, vs. the realm's "Vai trò ứng dụng"),
 * and every write flow (add/revoke/reset here are Assignment-grant writes, not
 * the realm's Keycloak-identity issue/reset/delete) — those keep their own
 * hooks and dialogs (`useScopedAccountWrites`, `useBulkLevelAssign`,
 * `ScopedAccountDialogs`, `BulkLevelAssignDialog`), unchanged from EAA-1839.
 *
 * BACKEND GAPS this screen still degrades honestly rather than faking — each
 * documented at the component that renders it: no `search` param on
 * `GET /organizations/{id}/accounts` (`searchDisabledReason` below), no CSV
 * export endpoint, the "Chưa đăng nhập lần nào" issue facet (`VALIDATION_ERROR`
 * server-side — see `useScopedAccountFacets`), and the three bulk actions with
 * no bulk endpoint (`ScopedAccountsBulkBar`'s old docstring, now folded into
 * the `bulkActions` list below).
 */
export function ScopedAccountsPage() {
    const { t } = useTranslation();
    const can = useCan();
    const vm = useScopedAccounts();
    const writes = useScopedAccountWrites(vm);
    const bulk = useBulkLevelAssign(vm);
    // EAA-1820 (frame 85, row kebab): "Chuyển đơn vị" — see `useRowTransfer`'s
    // docstring for why this is a per-row move, not a bulk action.
    const rowTransfer = useRowTransfer(vm.invalidateList);
    const canViewAudit = can('console.audit.view');

    const [tab, setTab] = useState<AccountsTab>('accounts');

    if (!vm.canView) return null;


    const bulkActions: AccountsBulkAction[] = [
        {
            key: 'assign-level',
            label: t('console.hierarchy.scopedAccounts.bulkBar.assignLevelButton'),
            onClick: bulk.openDialog,
            disabled: Boolean(vm.bulkLevelDisabledReason),
            disabledReason: vm.bulkLevelDisabledReason ?? undefined,
        },
        {
            key: 'change-org',
            label: t('console.hierarchy.scopedAccounts.bulkBar.changeOrgButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noBulkChangeOrg'),
        },
        {
            key: 'reset-password',
            label: t('console.hierarchy.scopedAccounts.bulkBar.resetPasswordButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noBulkReset'),
        },
        {
            key: 'deactivate',
            label: t('console.hierarchy.scopedAccounts.bulkBar.deactivateButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noBulkDeactivate'),
        },
    ];

    const bulkNotes: string[] = [];
    if (vm.crossOrgSelectedCount > 0) {
        bulkNotes.push(
            t('console.hierarchy.scopedAccounts.bulkBar.crossOrgNote', {
                value: vm.crossOrgSelectedCount.toLocaleString('vi-VN'),
            })
        );
    }
    if (vm.selectAllCapped) {
        bulkNotes.push(
            t('console.hierarchy.scopedAccounts.bulkBar.selectAllCappedNote', {
                value: vm.maxSelectAllRows.toLocaleString('vi-VN'),
            })
        );
    }
    if (vm.bulkLevelDisabledReason) bulkNotes.push(vm.bulkLevelDisabledReason);

    let body: React.ReactNode;
    if (!vm.orgId) {
        body = (
            <EmptyState
                title={t('console.accounts.scopedPage.noOrgSelectedTitle')}
                description={t('console.accounts.scopedPage.noOrgSelectedDescription')}
            />
        );
    } else if (vm.loading) {
        body = (
            <div role="status" aria-live="polite">
                <span className="portal-sr-only">{t('common.loading')}</span>
                <SkeletonTable rows={8} columns={TABLE_COLUMNS} />
            </div>
        );
    } else if (vm.isError) {
        body = (
            <p className="portal-admin-error">
                {t('console.accounts.scopedPage.loadErrorDescription')}{' '}
                <button type="button" className="portal-btn" onClick={() => void vm.listQuery.refetch()}>
                    {t('console.accounts.page.retryButton')}
                </button>
            </p>
        );
    } else if (vm.totalCount === 0) {
        body = (
            <EmptyState
                title={vm.filtersActive ? t('console.accounts.scopedPage.noMatchTitle') : t('console.accounts.scopedPage.emptyOrgTitle')}
                description={
                    vm.filtersActive
                        ? t('console.accounts.scopedPage.noMatchDescription')
                        : t('console.accounts.scopedPage.emptyOrgDescription')
                }
            />
        );
    } else {
        body = (
            <>
                <AccountsBulkBar
                    selectedCount={vm.selectedCount}
                    selectAllMatching={{
                        totalCount: vm.totalCount,
                        onSelectAll: () => void vm.selectAllMatching(),
                        busy: vm.selectAllBusy,
                        allSelected: vm.selectedCount >= vm.totalCount,
                    }}
                    actions={bulkActions}
                    onClear={vm.clearSelection}
                    notes={bulkNotes}
                />
                <ScopedAccountsTable vm={vm} writes={writes} onTransfer={rowTransfer.open} />
                <AccountsPagination
                    page={vm.page}
                    totalPages={vm.totalPages}
                    pageSize={vm.pageSize}
                    onPageSizeChange={vm.setPageSize}
                    rangeFrom={vm.rangeFrom}
                    rangeTo={vm.rangeTo}
                    total={vm.totalCount}
                    onGoTo={vm.setPage}
                    pageSizeOptions={vm.pageSizeOptions}
                />
            </>
        );
    }

    return (
        <>
            <AccountsScreen
                context="org"
                title={t('console.hierarchy.scopedAccounts.header.title')}
                subtitle={t('console.accounts.scopedPage.subtitle', {
                    count: vm.totalCount,
                    org: vm.orgName ?? t('console.hierarchy.scopedAccounts.header.scopeFallback'),
                })}
                headerActions={
                    <>
                        <button type="button" className="portal-btn" disabled title={t('console.hierarchy.scopedAccounts.header.csvUnavailable')}>
                            {t('console.hierarchy.scopedAccounts.header.exportCsv')}
                        </button>
                        {vm.canManage && (
                            <button type="button" className="portal-btn portal-btn-primary" onClick={writes.openAdd}>
                                {t('console.hierarchy.scopedAccounts.header.addAccountButton')}
                            </button>
                        )}
                    </>
                }
                tab={tab}
                onTabChange={setTab}
                showAuditTab={canViewAudit}
                auditPanel={<AccountAuditPanel />}
                scopeBar={{
                    orgLabel: vm.orgName ?? t('console.hierarchy.scopedAccounts.header.scopeFallback'),
                    includeChildren: vm.filters.includeChildren,
                    onToggleIncludeChildren: vm.toggleIncludeChildren,
                    backHref: '/console/hierarchy',
                }}
                facetGroups={vm.facetGroups}
                onSelectFacet={vm.selectFacet}
                hasActiveFilters={vm.filtersActive}
                onClearFilters={vm.clearFilters}
                search=""
                onSearchChange={() => {}}
                searchDisabledReason={t('console.hierarchy.scopedAccounts.header.searchUnavailable')}
                activeFilterCount={
                    (vm.filters.issue ? 1 : 0) +
                    (vm.filters.levelId ? 1 : 0) +
                    (vm.filters.childOrgId ? 1 : 0) +
                    (vm.filters.isActive !== null ? 1 : 0)
                }
                rangeLabel={t('console.hierarchy.scopedAccounts.pagination.rangeLabel', {
                    from: vm.rangeFrom.toLocaleString('vi-VN'),
                    to: vm.rangeTo.toLocaleString('vi-VN'),
                    total: vm.totalCount.toLocaleString('vi-VN'),
                })}
                body={body}
            />

            <ScopedAccountDialogs vm={vm} writes={writes} />
            <BulkLevelAssignDialog vm={vm} bulk={bulk} />
            <RowTransferDialog vm={rowTransfer} />
        </>
    );
}

export default ScopedAccountsPage;
